import { useLocale, useTranslation } from '@multaqa/i18n'
import { MoneyInput } from '@multaqa/ui/components/ui/money-input'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { useState } from 'react'
import { COMPANY_CURRENCIES } from '@/lib/company-currencies'
import type { CompanySettingResource } from './api'
import { useUpdateCompanySettings } from './api'
import { SettingsCard } from './company-settings-card'
import { SettingsSelectField } from './company-settings-fields'

export function CompanyApprovalsCard({
  groupId,
  companyId,
  settings,
}: {
  groupId: string
  companyId: string
  settings: CompanySettingResource
}) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const update = useUpdateCompanySettings()
  const [editing, setEditing] = useState(false)
  const [amount, setAmount] = useState(settings.require_approval_above_amount ?? '')
  const [currency, setCurrency] = useState(settings.approval_threshold_currency ?? '')
  const [message, setMessage] = useState<string | null>(null)

  function reset() {
    setAmount(settings.require_approval_above_amount ?? '')
    setCurrency(settings.approval_threshold_currency ?? '')
    setMessage(null)
  }

  function save() {
    if (Boolean(amount.trim()) !== Boolean(currency)) {
      setMessage(t('companies.settings.form.currencyRequired'))
      return
    }
    update.mutate(
      {
        groupId,
        companyId,
        input: {
          require_approval_above_amount: amount.trim() || null,
          approval_threshold_currency: amount.trim() ? currency : null,
        },
      },
      {
        onSuccess: () => {
          setEditing(false)
          setMessage(null)
          toast.success(t('state.saved'))
        },
        onError: () => setMessage(t('companies.settings.form.saveFailed')),
      },
    )
  }

  return (
    <SettingsCard
      testId="company-settings-approvals"
      title={t('companies.settings.groups.approvals')}
      description={t('companies.settings.groups.approvalsDescription')}
      editing={editing}
      pending={update.isPending}
      error={Boolean(update.error)}
      message={message}
      onEdit={() => {
        reset()
        setEditing(true)
      }}
      onCancel={() => {
        reset()
        setEditing(false)
      }}
      onSave={save}
    >
      {editing ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-1.5">
            <label htmlFor="company-settings-approval-amount" className="text-body">
              {t('companies.settings.fields.approvalThreshold')}
            </label>
            <MoneyInput
              id="company-settings-approval-amount"
              value={amount}
              locale={locale}
              onChange={setAmount}
            />
          </div>
          <SettingsSelectField
            id="company-settings-approval-currency"
            label={t('companies.settings.fields.approvalCurrency')}
            value={currency}
            options={COMPANY_CURRENCIES.map((value) => ({ value, label: value }))}
            onChange={setCurrency}
          />
        </div>
      ) : (
        <div className="grid gap-x-6 sm:grid-cols-2">
          <Value
            label={t('companies.settings.fields.approvalThreshold')}
            value={settings.require_approval_above_amount ?? t('companies.common.empty')}
          />
          <Value
            label={t('companies.settings.fields.approvalCurrency')}
            value={settings.approval_threshold_currency ?? t('companies.common.empty')}
          />
        </div>
      )}
    </SettingsCard>
  )
}

function Value({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-b border-border py-3">
      <div className="text-label text-muted-foreground font-mono uppercase">{label}</div>
      <div className="text-body font-mono">{value}</div>
    </div>
  )
}
